import PropTypes from 'prop-types';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const Header = ({ newsFeed, onClose }) => {
	const creationDatetime = new Date(
		newsFeed?.event_time || Date.now()
	).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});

	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'flex-start',
				padding: '16px 18px 14px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
				flexShrink: 0,
			}}
		>
			<Box>
				<Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
					{`${newsFeed.event?.event_title}`}
				</Typography>

				<Typography
					sx={{ fontSize: '12px', color: 'text.secondary', mt: '2px' }}
				>
					{`${creationDatetime} ${creationDatetime && newsFeed.event?.event_subtitle ? ' · ' : ''} ${newsFeed.event?.event_subtitle}`}
				</Typography>
			</Box>

			<IconButton
				size='small'
				onClick={onClose}
				sx={{
					width: 28,
					height: 28,
					borderRadius: '6px',
					border: '0.5px solid',
					borderColor: 'border.light',
					color: 'text.secondary',
					flexShrink: 0,
					'&:hover': { backgroundColor: 'background.subtle' },
				}}
			>
				<CloseIcon sx={{ fontSize: 20 }} />
			</IconButton>
		</Box>
	);
};

Header.propTypes = {
	newsFeed: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
};

const SectionLabel = ({ text }) => (
	<Typography
		sx={{
			fontSize: '11px',
			fontWeight: 600,
			textTransform: 'uppercase',
			color: 'text.secondary',
			mb: '6px',
		}}
	>
		{text}
	</Typography>
);

SectionLabel.propTypes = {
	text: PropTypes.string.isRequired,
};

const DetailSection = ({ event_context }) => {
	const labelMap = {
		lp_title: 'LP',
		indication: 'Indication',
		conditions: 'Conditions',
		pipeline_state: 'Pipeline state',
		sessions_today: 'Sessions today',
		total_time: 'Total time',
		sections_accessed: 'Sections accessed',
		vs_cohort_average: 'vs. cohort average',
	};

	return (
		<Box>
			<SectionLabel text={'Details'} />

			<Box
				sx={{
					backgroundColor: 'background.subtle',
					borderRadius: '8px',
					padding: '8px 10px',
				}}
			>
				{Object.entries(event_context).map(([key, value]) => (
					<Box
						key={key}
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							py: '3px',
						}}
					>
						<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
							{labelMap[key] ||
								key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
						</Typography>

						<Typography
							sx={{
								fontSize: '12px',
								fontWeight: 700,
								color: 'text.primary',
								textAlign: 'right',
								maxWidth: '60%',
							}}
						>
							{value}
						</Typography>
					</Box>
				))}
			</Box>
		</Box>
	);
};

DetailSection.propTypes = {
	event_context: PropTypes.object.isRequired,
};

const ActionDetails = ({ title, description }) => (
	<Box
		sx={{
			backgroundColor: 'vdr.bg',
			border: '0.5px solid',
			borderColor: 'vdr.border',
			borderRadius: '8px',
			padding: '12px 14px',
			mt: 1,
		}}
	>
		<Typography
			sx={{
				fontSize: '11px',
				fontWeight: 600,
				textTransform: 'uppercase',
				color: 'vdr.text',
				mb: '4px',
			}}
		>
			↔ {title}
		</Typography>

		<Typography sx={{ fontSize: '11px', color: 'vdr.text' }}>
			{description}
		</Typography>
	</Box>
);

ActionDetails.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
};

const ActionButton = ({ label, link }) => {
	return (
		<Button
			key={`news-feed-action-button`}
			href={`#${link}`}
			variant='outlined'
			size='small'
			sx={{
				fontSize: '12px',
				fontWeight: 500,
				padding: '7px 14px',
				borderRadius: '6px',
				backgroundColor: 'vdr.bg',
				color: 'vdr.text',
				borderColor: 'vdr.border',
				cursor: 'pointer',
				'&:hover': { backgroundColor: 'vdr.hover' },
			}}
		>
			{`↔ ${label}`}
		</Button>
	);
};

ActionButton.propTypes = {
	label: PropTypes.string.isRequired,
	link: PropTypes.string.isRequired,
};

const NewsFeedDrawer = ({ newsFeed, open, onClose }) => {
	return (
		<>
			{/* backdrop */}
			<Box
				data-testid='news-feed-backdrop'
				onClick={onClose}
				sx={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(0,0,0,0.25)',
					opacity: open ? 1 : 0,
					pointerEvents: open ? 'auto' : 'none',
					transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
					zIndex: 1200,
				}}
			/>
			{/* panel */}
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					width: 520,
					backgroundColor: 'background.paper',
					transform: open ? 'translateX(0)' : 'translateX(100%)',
					transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1)',
					zIndex: 1201,
					display: 'flex',
					flexDirection: 'column',
					boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.08)' : 'none',
				}}
			>
				<Header newsFeed={newsFeed} onClose={onClose} />

				{/* Body */}
				<Box
					sx={{
						padding: '16px 18px',
						overflowY: 'auto',
						display: 'flex',
						flexDirection: 'column',
						gap: '14px',
					}}
				>
					{newsFeed.event?.event_detail && (
						<Box>{newsFeed.event?.event_detail}</Box>
					)}

					{/* Detail */}
					{newsFeed.event?.event_context &&
						Object.keys(newsFeed.event.event_context).length > 0 && (
							<DetailSection event_context={newsFeed.event.event_context} />
						)}

					{/* Action details */}
					{newsFeed.action?.action_title &&
						newsFeed.action?.action_description && (
							<ActionDetails
								title={newsFeed.action.action_title}
								description={newsFeed.action.action_description}
							/>
						)}

					{newsFeed.action?.action_link && (
						<Box
							sx={{
								display: 'flex',
								gap: 1,
								flexWrap: 'wrap',
							}}
						>
							<ActionButton
								key={`news-feed-action-button`}
								label={'Action Button'}
								link={newsFeed.action?.action_link}
							/>
						</Box>
					)}
				</Box>
			</Box>
		</>
	);
};

NewsFeedDrawer.propTypes = {
	newsFeed: PropTypes.object,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default NewsFeedDrawer;
